import { Injectable, signal } from '@angular/core';

/**
 * Screen zoom: lets a small screen (an 11" Sunmi) zoom the whole KDS out so more of it fits.
 *
 * Per device, not per restaurant: it is about the screen in front of you, so it lives in
 * localStorage under its own key and survives a unit change.
 *
 * Applied as CSS `zoom` on <html>, which scales everything - px and rem alike (the styles
 * use both, so a root font-size would only shrink half the screen). The one thing zoom does
 * not handle for us is full-height layout: 100dvh inside a zoomed page covers only `zoom`
 * of the screen, so the full-height shells divide by --kds-zoom (see app-layout and login).
 */
export const ZOOM_MIN = 60;
export const ZOOM_MAX = 120;
export const ZOOM_STEP = 10;
export const ZOOM_DEFAULT = 100;
const STORAGE_KEY = 'kds_zoom_percent';

export function clampZoom(percent: number): number {
  if (!Number.isFinite(percent)) return ZOOM_DEFAULT;
  const stepped = Math.round(percent / ZOOM_STEP) * ZOOM_STEP;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, stepped));
}

@Injectable({ providedIn: 'root' })
export class ZoomService {
  readonly percent = signal<number>(ZOOM_DEFAULT);

  constructor() {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      stored = null;
    }
    this.apply(stored === null ? ZOOM_DEFAULT : clampZoom(Number(stored)), false);
  }

  zoomIn(): void {
    this.set(this.percent() + ZOOM_STEP);
  }

  zoomOut(): void {
    this.set(this.percent() - ZOOM_STEP);
  }

  reset(): void {
    this.set(ZOOM_DEFAULT);
  }

  set(percent: number): void {
    this.apply(clampZoom(percent), true);
  }

  private apply(percent: number, persist: boolean): void {
    this.percent.set(percent);
    const html = document.documentElement;
    const factor = percent / 100;
    html.style.setProperty('--kds-zoom', String(factor));
    // 100% clears it rather than writing zoom: 1, so a screen that never zooms renders
    // exactly as it did before this setting existed.
    if (percent === ZOOM_DEFAULT) html.style.removeProperty('zoom');
    else html.style.setProperty('zoom', String(factor));
    if (!persist) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(percent));
    } catch {
      /* private mode or full storage: the zoom still applies until the app restarts */
    }
  }
}
